import { Component, OnInit, OnDestroy, HostListener, ChangeDetectionStrategy, ChangeDetectorRef, NgZone } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { Subscription } from 'rxjs';
import { ModalService, ModalConfig, ModalType, ModalSize } from '../../services/modal.service';
import { NotificationService } from '../../services/notification.service';

/**
 * Production-Ready Modal Component
 * Reusable modal with guaranteed rendering and performance optimization
 * 
 * Features:
 * - OnPush change detection for performance
 * - Explicit change detection triggering
 * - Zone.js integration
 * - Memory leak prevention
 * - Error boundaries
 * - Accessibility support
 */
@Component({
  selector: 'app-modal',
  imports: [CommonModule, FormsModule],
  templateUrl: './modal.component.html',
  styleUrl: './modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush, // Performance optimization
  animations: [
    trigger('fadeIn', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('150ms ease-out', style({ opacity: 1 })) // Faster for instant feel
      ]),
      transition(':leave', [
        animate('100ms ease-in', style({ opacity: 0 }))
      ])
    ]),
    trigger('slideIn', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(-10px) scale(0.98)' }),
        animate('200ms ease-out', style({ opacity: 1, transform: 'translateY(0) scale(1)' })) // Faster
      ]),
      transition(':leave', [
        animate('150ms ease-in', style({ opacity: 0, transform: 'translateY(-5px) scale(0.98)' }))
      ])
    ])
  ]
})
export class ModalComponent implements OnInit, OnDestroy {
  isOpen = false;
  config: ModalConfig | null = null;
  queueLength = 0;
  isProcessingAction = false; // Double-click protection
  inputValue = ''; // For PROMPT type modals
  private initialInputValue = ''; // Snapshot for requireInputChange gating
  copyButtonLabel = 'Copy';

  get isConfirmDisabled(): boolean {
    if (this.isProcessingAction) { return true; }
    if (!this.config || this.config.type !== ModalType.PROMPT || !this.config.requireInputChange) {
      return false;
    }
    const current = (this.inputValue || '').trim();
    const initial = (this.initialInputValue || '').trim();
    return !current || current === initial;
  }
  
  // Expose enums to template
  ModalType = ModalType;
  ModalSize = ModalSize;
  
  private subscriptions: Subscription[] = [];
  private copyLabelResetTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private modalService: ModalService,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone,
    private notificationService: NotificationService
  ) {}

  ngOnInit(): void {
    // Subscribe to modal state changes
    const stateSub = this.modalService.modalState$.subscribe(state => {
      // Run inside zone to ensure change detection
      this.ngZone.run(() => {
        this.isOpen = state.isOpen;
        this.config = state.config;
        this.queueLength = state.queueLength || 0;

        if (!state.isOpen || state.config?.showCopyButton !== true) {
          this.resetCopyButtonLabel();
        }
        
        // Initialize input value for PROMPT modals
        if (state.config?.type === ModalType.PROMPT) {
          const initial = state.config.inputValue || '';
          this.inputValue = initial;
          this.initialInputValue = initial;
        }
        
        // Explicitly mark for check with OnPush strategy
        this.cdr.markForCheck();
        
        // Force immediate detection if modal is opening
        if (state.isOpen) {
          this.cdr.detectChanges();
        }
      });
    });
    this.subscriptions.push(stateSub);

    // Subscribe to force update events
    const updateSub = this.modalService.forceUpdate.subscribe(() => {
      this.ngZone.run(() => {
        this.cdr.markForCheck();
        this.cdr.detectChanges();
      });
    });
    this.subscriptions.push(updateSub);
  }

  ngOnDestroy(): void {
    this.clearCopyLabelResetTimer();
    this.subscriptions.forEach(sub => sub.unsubscribe());
    this.subscriptions = [];
  }

  async onCopy(): Promise<void> {
    try {
      const textToCopy = this.getCopyText();
      if (!textToCopy.trim()) {
        return;
      }

      if (!navigator?.clipboard?.writeText) {
        this.notificationService.showError('Failed to copy to clipboard');
        return;
      }

      await navigator.clipboard.writeText(textToCopy);
      this.copyButtonLabel = 'Copied';
      this.cdr.detectChanges();
      this.scheduleCopyButtonReset();
      this.notificationService.showSuccess('Copied to clipboard');
    } catch (error) {
      this.notificationService.showError('Failed to copy to clipboard');
    }
  }

  /**
   * Handle ESC key press (with error boundary)
   */
  @HostListener('document:keydown', ['$event'])
  handleKeydown(event: KeyboardEvent): void {
    try {
      if (event.key === 'Escape' && this.isOpen && this.config?.allowEscapeKey) {
        event.preventDefault();
        event.stopPropagation();
        this.onCancel();
      }
    } catch (error) {
    }
  }

  /**
   * Handle overlay click (click outside modal)
   */
  onOverlayClick(): void {
    try {
      if (this.config?.allowOutsideClick) {
        this.onCancel();
      }
    } catch (error) {
      // Fallback: force close
      this.modalService.close();
    }
  }

  /**
   * Handle confirm button click with double-click protection
   */
  onConfirm(): void {
    // Guard: Prevent double-click (production-grade protection)
    if (this.isProcessingAction) {
      return;
    }
    
    try {
      this.isProcessingAction = true;
      this.cdr.detectChanges(); // Update UI immediately to disable button
      
      this.ngZone.run(() => {
        // For PROMPT type, call onSubmit with input value
        if (this.config?.type === ModalType.PROMPT && this.config.onSubmit) {
          this.config.onSubmit(this.inputValue);
          this.modalService.close();
        } else {
          this.modalService.confirm();
        }
      });
      
      // Reset after short delay
      setTimeout(() => {
        this.isProcessingAction = false;
        this.cdr.detectChanges();
      }, 300);
    } catch (error) {
      this.isProcessingAction = false;
      this.cdr.detectChanges();
      // Fallback: force close
      this.modalService.close();
    }
  }

  /**
   * Handle cancel button click with double-click protection
   */
  onCancel(): void {
    // Guard: Prevent double-click (production-grade protection)
    if (this.isProcessingAction) {
      return;
    }
    
    try {
      this.isProcessingAction = true;
      this.cdr.detectChanges(); // Update UI immediately to disable button
      
      this.ngZone.run(() => {
        this.modalService.cancel();
      });
      
      // Reset after short delay
      setTimeout(() => {
        this.isProcessingAction = false;
        this.cdr.detectChanges();
      }, 300);
    } catch (error) {
      this.isProcessingAction = false;
      this.cdr.detectChanges();
      // Fallback: force close
      this.modalService.close();
    }
  }

  /**
   * Handle close button click
   */
  onClose(): void {
    try {
      this.ngZone.run(() => {
        this.modalService.close();
      });
    } catch (error) {
      // Fallback: force close
      this.modalService.close();
    }
  }

  /**
   * Stop event propagation (prevent overlay click when clicking modal content)
   */
  onContentClick(event: Event): void {
    event.stopPropagation();
  }

  /**
   * Check if modal should show buttons
   */
 get showButtons(): boolean {
  // Don't show buttons for auto-dismissing modals
  if (this.config?.autoDismiss) {
    return false;
  }
  return this.config?.type === ModalType.ALERT || 
         this.config?.type === ModalType.CONFIRMATION ||
         this.config?.type === ModalType.PROMPT;
}

  /**
   * Check if modal is a confirmation type (includes PROMPT for button layout)
   */
  get isConfirmation(): boolean {
    return this.config?.type === ModalType.CONFIRMATION || 
           this.config?.type === ModalType.PROMPT;
  }

  /**
   * Get modal size class
   */
  get sizeClass(): string {
    return `modal-${this.config?.size || ModalSize.MEDIUM}`;
  }

  private getCopyText(): string {
    const value = this.config?.copyText ?? this.config?.message ?? '';
    return typeof value === 'string' ? value : String(value);
  }

  private scheduleCopyButtonReset(): void {
    this.clearCopyLabelResetTimer();
    this.copyLabelResetTimer = setTimeout(() => {
      this.copyButtonLabel = 'Copy';
      this.cdr.detectChanges();
      this.copyLabelResetTimer = null;
    }, 1200);
  }

  private clearCopyLabelResetTimer(): void {
    if (this.copyLabelResetTimer) {
      clearTimeout(this.copyLabelResetTimer);
      this.copyLabelResetTimer = null;
    }
  }

  private resetCopyButtonLabel(): void {
    this.copyButtonLabel = 'Copy';
    this.clearCopyLabelResetTimer();
  }
}
